import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';

interface BarChartProps {
  data: any[];
  xKey: string;
  bars: {
    dataKey: string;
    name: string;
    color: string;
  }[];
  height?: number;
  className?: string;
  layout?: 'horizontal' | 'vertical';
  // When true, all bars share a stackId so segments stack on top of each
  // other instead of rendering as a multi-bar group. Use for status
  // breakdowns where the meaningful number is the daily total.
  stacked?: boolean;
  formatTooltip?: (value: any, name: string) => [string, string];
  formatXAxisLabel?: (value: any) => string;
  formatYAxisLabel?: (value: any) => string;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  xKey,
  bars,
  height = 300,
  className = '',
  layout = 'vertical',
  stacked = false,
  formatTooltip,
  formatXAxisLabel,
  formatYAxisLabel
}) => {
  const { isDark } = useTheme();

  // Dark mode colors
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.06)' : '#f0f0f0';
  const axisColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const tickColor = isDark ? '#71717a' : '#6b7280';
  const tooltipBg = isDark ? '#18181b' : 'white';
  const tooltipBorder = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const tooltipLabelColor = isDark ? '#f4f4f5' : '#374151';
  const defaultTooltipFormatter = (value: any, name: string) => {
    if (name.toLowerCase().includes('revenue') || name.toLowerCase().includes('$')) {
      return [`$${Number(value).toLocaleString()}`, name];
    }
    return [Number(value).toLocaleString(), name];
  };

  const defaultAxisFormatter = (value: any) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return String(value);
  };

  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          layout={layout}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            type={layout === 'vertical' ? 'number' : 'category'}
            dataKey={layout === 'vertical' ? undefined : xKey}
            tick={{ fontSize: 12, fill: tickColor }}
            tickFormatter={layout === 'vertical' ? (formatXAxisLabel || defaultAxisFormatter) : formatXAxisLabel}
            stroke={axisColor}
          />
          <YAxis
            type={layout === 'vertical' ? 'category' : 'number'}
            dataKey={layout === 'vertical' ? xKey : undefined}
            tick={{ fontSize: 12, fill: tickColor }}
            tickFormatter={layout === 'vertical' ? undefined : (formatYAxisLabel || defaultAxisFormatter)}
            stroke={axisColor}
            width={layout === 'vertical' ? 100 : undefined}
          />
          <Tooltip
            formatter={formatTooltip || defaultTooltipFormatter}
            labelStyle={{ color: tooltipLabelColor, fontWeight: 'medium' }}
            contentStyle={{
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '8px',
              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          {bars.length > 1 && <Legend />}
          {bars.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              fill={bar.color}
              name={bar.name}
              stackId={stacked ? 'a' : undefined}
              radius={[2, 2, 2, 2]}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}; 